import React, { useState } from "react";
import { X } from "lucide-react";
import { useSite, resolveImageUrl } from "../context/SiteContext";

export default function Team() {
  const { site } = useSite();
  const team = site?.team || [];
  const [active, setActive] = useState(null);

  return (
    <section id="team" className="relative py-24 md:py-32" data-testid="team-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-10 mb-16 md:mb-20 items-end">
          <div className="md:col-span-7">
            <p className="overline text-[#9F5C45] mb-6">03 — Our therapists</p>
            <h2 className="font-serif font-light text-[#1C1917] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              People you’ll genuinely <em className="italic text-[#3A5A40]">want</em> in your corner.
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <p className="text-[#57534E] text-base md:text-lg leading-relaxed">
              Every clinician is fully licensed, deeply experienced and personally
              vetted by our clinical director. We match you carefully.
            </p>
          </div>
        </div>

        {team.length === 0 ? (
          <p className="text-[#57534E] italic font-serif text-xl">Therapist bios coming soon.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
            {team.map((m, i) => (
              <button
                key={m.id || i}
                onClick={() => m.bio && setActive(m)}
                data-testid={`team-member-${i}`}
                className={`group text-left ${m.bio ? "cursor-pointer" : "cursor-default"}`}
              >
                <div className="overflow-hidden mb-5 aspect-[3/4] bg-[#F4F1EA] relative">
                  {m.image_url && (
                    <img
                      src={resolveImageUrl(m.image_url)}
                      alt={m.name}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-[1.03]"
                    />
                  )}
                  {m.bio && (
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/85 via-[#1C1917]/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
                      <span className="overline text-[#FDFBF7]">Read bio →</span>
                    </div>
                  )}
                </div>
                <h3 className="font-serif text-2xl text-[#1C1917] leading-tight">{m.name}</h3>
                {m.title && (
                  <p className="text-xs uppercase tracking-[0.18em] text-[#57534E] mt-2">{m.title}</p>
                )}
                {m.spec && (
                  <p className="text-sm text-[#3A5A40] mt-2 italic font-serif uppercase tracking-wider">{m.spec}</p>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {active && <BioModal member={active} onClose={() => setActive(null)} />}
    </section>
  );
}

function BioModal({ member, onClose }) {
  return (
    <div
      role="dialog"
      data-testid="team-bio-modal"
      className="fixed inset-0 z-[70] bg-[#1C1917]/70 backdrop-blur-sm flex items-center justify-center p-6 animate-in fade-in"
      onClick={onClose}
    >
      <div
        className="relative bg-[#FDFBF7] max-w-4xl w-full max-h-[90vh] overflow-y-auto grid md:grid-cols-[280px_1fr]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-[#F4F1EA] aspect-[3/4] md:aspect-auto md:h-full">
          {member.image_url && (
            <img
              src={resolveImageUrl(member.image_url)}
              alt={member.name}
              className="w-full h-full object-cover"
            />
          )}
        </div>
        <div className="p-8 md:p-12 relative">
          <button
            onClick={onClose}
            data-testid="team-bio-close"
            className="absolute top-4 right-4 p-2 text-[#57534E] hover:text-[#1C1917] transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <p className="overline text-[#9F5C45] mb-3">Therapist</p>
          <h3 className="font-serif text-4xl md:text-5xl text-[#1C1917] tracking-tight mb-3 leading-tight">
            {member.name}
          </h3>
          {member.title && (
            <p className="text-xs uppercase tracking-[0.2em] text-[#57534E] mb-2">{member.title}</p>
          )}
          {member.spec && (
            <p className="font-serif italic text-[#3A5A40] text-lg mb-8 uppercase tracking-wider">{member.spec}</p>
          )}
          {member.bio && (
            <div className="border-t border-[#E2DCD0] pt-6 text-[#1C1917] text-base leading-relaxed whitespace-pre-line">
              {member.bio}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
